/**
 * In-memory SWR-style cache for admin list pages.
 * Survives route unmounts so sidebar navigation feels instant.
 */

const store = new Map();

/**
 * @typedef {{ data: any, updatedAt: number, promise: Promise<any> | null }} CacheEntry
 */

export function peekCache(key) {
  if (!key) return null;
  return store.get(key) || null;
}

export function setCache(key, data) {
  if (!key) return;
  const prev = store.get(key);
  store.set(key, {
    data,
    updatedAt: Date.now(),
    promise: prev?.promise || null,
  });
}

export function invalidateCache(keyOrPrefix) {
  if (!keyOrPrefix) {
    store.clear();
    return;
  }

  if (store.has(keyOrPrefix)) {
    store.delete(keyOrPrefix);
  }

  for (const key of [...store.keys()]) {
    if (key.startsWith(keyOrPrefix)) {
      store.delete(key);
    }
  }
}

/**
 * Read-through cache with in-flight dedupe.
 * @returns {Promise<{ data: any, fromCache: boolean, stale: boolean }>}
 */
export async function cachedFetch(key, fetcher, { staleTime = 60_000, force = false } = {}) {
  const now = Date.now();
  const entry = store.get(key);

  if (!force && entry?.data != null && now - entry.updatedAt < staleTime) {
    return { data: entry.data, fromCache: true, stale: false };
  }

  if (!force && entry?.promise) {
    const data = await entry.promise;
    return {
      data,
      fromCache: false,
      stale: Boolean(entry.data != null && now - entry.updatedAt >= staleTime),
    };
  }

  const promise = Promise.resolve()
    .then(() => fetcher())
    .then((data) => {
      store.set(key, { data, updatedAt: Date.now(), promise: null });
      return data;
    })
    .catch((error) => {
      const current = store.get(key);
      if (current?.promise === promise) {
        store.set(key, { ...current, promise: null });
      }
      throw error;
    });

  store.set(key, {
    data: entry?.data,
    updatedAt: entry?.updatedAt || 0,
    promise,
  });

  const data = await promise;
  return {
    data,
    fromCache: false,
    stale: false,
  };
}
